import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, MessageCircle } from "lucide-react";
import { selecionarEmpresa, selecionarOrganizacao } from "../api/auth";
import { listarEmpresasAtivasDaOrganizacao, type EmpresaOrganizacaoOpcao } from "../api/empresas";
import { obterOrganizacoesAtivasConfig, type OrganizacaoConfig } from "../api/sistema";
import { useAuth } from "../auth/AuthContext";
import { empresasPermitidasUsuario } from "../utils/permissoes";
import { IconeCalendario, IconeChatGPT } from "../components/icons";
import { AvatarCadastro } from "../components/tabela/AvatarCadastro";
import { BancoImage } from "../components/ui/BancoImage";
import { BotaoIcone } from "../components/ui/BotaoIcone";
import { CampoPesquisa } from "../components/ui/CampoPesquisa";
import { useAcessoTela } from "../components/ui/AcessoTela";
import { Popover } from "../components/ui/Popover";
import { limparPeriodoGlobal, salvarPeriodoGlobal, usePeriodoGlobal } from "../utils/periodoGlobal";
import { FiltroPeriodoModal } from "./FiltroPeriodoModal";
import { MenuUsuario } from "./MenuUsuario";
import { useAcoesTopo } from "./AcoesTopoContext";
import { useIdioma } from "../i18n/IdiomaProvider";
import { mostrarToastErro } from "../utils/toast";
import { recarregarIdentidadeVisualEmpresa } from "../theme/configVisual";
import { carregarConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";
import { listarColegasChat, type ColegaChat } from "../api/chatInterno";
import { urlAsset } from "../utils/assets";
import { EVENTO_ABRIR_SELETOR_EMPRESA, type DetalheAbrirSeletorEmpresa } from "./eventosLayout";
import { useMarcasEmpresa } from "../theme/useMarcasEmpresa";
import { ROTAS_RECURSOS_PLATAFORMA } from "./telasDisponiveis";
import styles from "./Header.module.css";

interface HeaderProps {
  nomeSistema?: string;
  buscaMenu?: string;
  onBuscaMenu?: (valor: string) => void;
  onAbrirMenu?: () => void;
  onAbrirIA?: () => void;
  iaVisivel?: boolean;
  iaAtiva?: boolean;
  tituloContextual?: string;
  subtituloContextual?: string;
  exibirMarcaRecolhida?: boolean;
}

const IcoCalendario = () => <IconeCalendario width={17} height={17} strokeWidth={1.7} />;
const IcoIA = () => <IconeChatGPT width={17} height={17} />;
const IcoSino = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);
const IcoBusca = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="9" cy="9" r="5.5" />
    <path d="m14 14 3 3" />
  </svg>
);

function nomeUsuario(nomePerfil?: string, email?: string): string {
  const nome = String(nomePerfil || "").trim();
  if (nome) return nome.split(" ")[0];
  const base = String(email || "Usuário").split("@")[0];
  return base.charAt(0).toUpperCase() + base.slice(1);
}

function obterSaudacao(): string {
  const hora = new Date().getHours();
  if (hora < 12) return "Bom dia";
  if (hora < 18) return "Boa tarde";
  return "Boa noite";
}

export function Header({
  buscaMenu = "",
  onBuscaMenu,
  onAbrirMenu,
  onAbrirIA,
  iaVisivel = true,
  iaAtiva = true,
  tituloContextual,
  subtituloContextual,
  exibirMarcaRecolhida = false,
  nomeSistema,
}: HeaderProps) {
  const { usuario, recarregarSessao } = useAuth();
  const { traduzir } = useIdioma();
  const { podeAbrirTela, avisoSemPermissao } = useAcessoTela();
  const { abrirChat, abrirNotificacoes, totalNotificacoes, naoLidasChat } = useAcoesTopo();
  const { marcaRecolhidaUrl } = useMarcasEmpresa();
  const periodoGlobal = usePeriodoGlobal();
  const nome = nomeUsuario(usuario?.nomePerfil, usuario?.email);
  const ehCliente = ["CLIENTE", "CLIENT"].includes(String(usuario?.tipoUsuario || "").toUpperCase());
  const [filialAberta, setFilialAberta] = useState(false);
  const [calendarioAberto, setCalendarioAberto] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaOrganizacaoOpcao[]>([]);
  const [organizacoes, setOrganizacoes] = useState<OrganizacaoConfig[]>([]);
  const [empresaTrocando, setEmpresaTrocando] = useState("");
  const [organizacaoTrocando, setOrganizacaoTrocando] = useState("");
  const [loginsOnline, setLoginsOnline] = useState<ColegaChat[]>([]);
  const ancoraSeletorRef = useRef<HTMLElement | null>(null);
  const codOrganizacaoAtiva = String(usuario?.selectedCodOrg || usuario?.codOrg || "");
  const podeTrocarTodas = usuario?.acessoGlobal === true || usuario?.tipoUsuario === "ADMIN_ORG";
  const podeUsarAgente = podeAbrirTela(ROTAS_RECURSOS_PLATAFORMA.agente);
  const podeUsarChat = podeAbrirTela(ROTAS_RECURSOS_PLATAFORMA.chat);
  const podeUsarCentralNotificacoes = podeAbrirTela(ROTAS_RECURSOS_PLATAFORMA.centralNotificacoes);

  useEffect(() => {
    if (!codOrganizacaoAtiva || codOrganizacaoAtiva === "todos") {
      setEmpresas([]);
      return;
    }
    void listarEmpresasAtivasDaOrganizacao(codOrganizacaoAtiva).then(setEmpresas).catch(() => setEmpresas([]));
  }, [codOrganizacaoAtiva]);

  useEffect(() => {
    if (usuario?.acessoGlobal !== true) {
      setOrganizacoes([]);
      return;
    }
    const carregarOrganizacoes = () => {
      void obterOrganizacoesAtivasConfig().then(setOrganizacoes).catch(() => setOrganizacoes([]));
    };
    carregarOrganizacoes();
    window.addEventListener("i9:organizacoes-atualizadas", carregarOrganizacoes);
    return () => window.removeEventListener("i9:organizacoes-atualizadas", carregarOrganizacoes);
  }, [usuario?.acessoGlobal]);

  useEffect(() => {
    if (ehCliente || !podeUsarChat) {
      setLoginsOnline([]);
      return;
    }
    let ativo = true;
    const carregar = async () => {
      const colegas = await listarColegasChat().catch(() => []);
      if (ativo) setLoginsOnline(colegas.filter((colega) => colega.online));
    };
    void carregar();
    const intervalo = window.setInterval(() => void carregar(), 30_000);
    return () => { ativo = false; window.clearInterval(intervalo); };
  }, [ehCliente, podeUsarChat, usuario?.codEmpresa, usuario?.codOrg]);

  useEffect(() => {
    const abrir = (evento: Event) => {
      const ancora = (evento as CustomEvent<DetalheAbrirSeletorEmpresa>).detail?.ancora;
      if (!ancora) return;
      ancoraSeletorRef.current = ancora;
      setFilialAberta(true);
    };
    window.addEventListener(EVENTO_ABRIR_SELETOR_EMPRESA, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_SELETOR_EMPRESA, abrir);
  }, []);

  useEffect(() => {
    const abrir = () => setCalendarioAberto(true);
    const fechar = () => setCalendarioAberto(false);
    window.addEventListener("system-tour-open-period", abrir);
    window.addEventListener("system-tour-close-period", fechar);
    return () => {
      window.removeEventListener("system-tour-open-period", abrir);
      window.removeEventListener("system-tour-close-period", fechar);
    };
  }, []);

  const empresasLiberadas = useMemo(() => {
    if (podeTrocarTodas) return empresas;
    const permitidas = empresasPermitidasUsuario(usuario);
    return permitidas === "todas" ? empresas : empresas.filter((empresa) => permitidas.includes(String(empresa.codigo)));
  }, [empresas, podeTrocarTodas, usuario]);
  const empresaAtual = empresasLiberadas.find(
    (empresa) => String(empresa.codigo) === String(usuario?.codEmpresa || "")
  );
  const marcaContextualUrl = empresaAtual?.logoUrl || marcaRecolhidaUrl;

  useEffect(() => {
    // A aba acompanha a empresa ativa, inclusive após o recarregamento das configurações visuais.
    document.title = empresaAtual?.nome?.trim() || nomeSistema || "i9 Soluções Digitais";
  }, [empresaAtual?.nome, nomeSistema]);

  async function trocarEmpresa(codigo: string | number) {
    if (empresaTrocando) return;
    setEmpresaTrocando(String(codigo));
    try {
      await selecionarEmpresa(codigo);
      await recarregarIdentidadeVisualEmpresa(codigo);
      await recarregarSessao();
      await carregarConfiguracoesSistemaPublicas();
      setFilialAberta(false);
      /* Cada tela carrega os dados no próprio ciclo: sem recarregar, listagens e
         quadros já montados continuariam exibindo os registros da empresa
         anterior até o operador navegar de novo. */
      window.location.reload();
    } catch (erro) {
      mostrarToastErro(traduzir(erro instanceof Error ? erro.message : "Não foi possível trocar a empresa."));
    } finally {
      setEmpresaTrocando("");
    }
  }

  async function trocarOrganizacao(codigo: string | number) {
    if (organizacaoTrocando) return;
    setOrganizacaoTrocando(String(codigo));
    try {
      await selecionarOrganizacao(codigo);
      await recarregarSessao();
      await carregarConfiguracoesSistemaPublicas();
      await recarregarIdentidadeVisualEmpresa();
      window.location.reload();
    } catch (erro) {
      mostrarToastErro(traduzir(erro instanceof Error ? erro.message : "Não foi possível trocar a organização."));
    } finally {
      setOrganizacaoTrocando("");
    }
  }

  const resumoPeriodo = periodoGlobal ? `${periodoGlobal.inicio} — ${periodoGlobal.fim}` : traduzir("Todos os períodos");

  return (
    <header className={`${styles.header} ${exibirMarcaRecolhida ? styles.headerComMarca : ""}`}>
      <div className={styles.esquerda}>
        {exibirMarcaRecolhida && (
          <span className={styles.marcaContextual} aria-hidden="true">
            <BancoImage className={styles.marcaContextualImagem} src={marcaContextualUrl} alt="" />
          </span>
        )}
        <div className={styles.saudacaoBloco}>
          {tituloContextual ? (
            <>
              <div className={styles.tituloContextual}>{traduzir(tituloContextual)}</div>
              {subtituloContextual && <span className={styles.subtituloContextual}>{traduzir(subtituloContextual)}</span>}
            </>
          ) : (
            <>
              <div className={styles.saudacao}>{traduzir(obterSaudacao())}, <span className={styles.nomeDestaque} data-i18n-db="true">{nome}</span></div>
              <span className={styles.saudacaoSub}>{traduzir("Bem-vindo(a) ao painel de gestão da sua organização.")}</span>
            </>
          )}
        </div>
      </div>

      <div className={styles.direita}>
        {loginsOnline.length > 0 && (
          <div className={styles.loginsOnline} data-tour="header-online" aria-label={`${loginsOnline.length} logins online`}>
            {/* Exibe cinco pessoas; o contador tem a cor da empresa selecionada. */}
            {loginsOnline.slice(0, 5).map((login) => (
              <div
                key={login.codLogin}
                className={`${styles.loginOnlineAvatar} tooltip`}
                data-tooltip={`${login.nome} · ${login.email}`}
                aria-label={`${login.nome} · ${login.email}`}
              >
                <AvatarCadastro nome={login.nome} foto={login.avatarId ? urlAsset(login.avatarId) : ""} tamanho={28} />
              </div>
            ))}
            {loginsOnline.length > 5 && <span className={styles.loginsOnlineExcedentes}>+{loginsOnline.length - 5}</span>}
          </div>
        )}

        <CampoPesquisa
          className={styles.buscaFilialField}
          data-tour="header-search"
          inputClassName={styles.buscaFilialInput}
          value={buscaMenu}
          placeholder="Pesquisar menu"
          onFocus={onAbrirMenu}
          onChange={(evento) => {
            onBuscaMenu?.(evento.target.value);
            if (evento.target.value.trim()) onAbrirMenu?.();
          }}
          aria-label="Pesquisar menu"
          iconeInicio={<IcoBusca />}
        />

        <Popover ancora={ancoraSeletorRef} aberto={filialAberta} onFechar={() => setFilialAberta(false)} largura={340} alinhamento="inicio" posicao="lateral">
          <div className={styles.buscaFilialPainel}>
            {usuario?.acessoGlobal === true && (
              <>
                <div className={styles.buscaFilialCabecalho}>{traduzir("Organizações")}</div>
                <div className={`${styles.buscaFilialLista} ${styles.buscaOrganizacaoLista}`}>
                  {organizacoes.map((organizacao) => (
                    <button key={organizacao.cod} type="button" className={styles.buscaFilialItem} disabled={organizacaoTrocando === String(organizacao.cod)} onClick={() => void trocarOrganizacao(organizacao.cod)}>
                      <AvatarCadastro nome={organizacao.organizacao} tamanho={24} />
                      <span className={styles.buscaFilialNome} data-i18n-db="true">{organizacao.organizacao}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className={styles.buscaFilialCabecalho}>{traduzir("Empresas da organização")}</div>
            <div className={styles.buscaFilialLista}>
              {empresasLiberadas.map((empresa) => (
                <button key={empresa.codigo} type="button" className={`${styles.buscaFilialItem} ${String(empresa.codigo) === String(usuario?.codEmpresa || "") ? styles.buscaFilialItemAtiva : ""}`} disabled={empresaTrocando === String(empresa.codigo)} onClick={() => void trocarEmpresa(empresa.codigo)}>
                  {String(empresa.codigo) === "todos" ? <span className={styles.empresaTodasIcone}><Building2 size={19} /></span> : <AvatarCadastro nome={empresa.nome} foto={empresa.logoUrl} tamanho={34} />}
                  <span className={styles.buscaFilialNome} data-i18n-db="true">{empresa.nome}</span>
                </button>
              ))}
            </div>
          </div>
        </Popover>

        <button type="button" className={`${styles.filtroDataTopo} ${calendarioAberto ? styles.filtroDataTopoAberto : ""} ${periodoGlobal ? styles.filtroDataTopoAtivo : ""}`} data-tour="filtro-data" aria-pressed={Boolean(periodoGlobal)} onClick={() => setCalendarioAberto(true)}>
          <span className={styles.filtroDataIcone}><IcoCalendario /></span>
          <span className={styles.filtroDataLabel}>{resumoPeriodo}</span>
        </button>

        <div className={styles.acoesTopo} data-tour="acoes-topo">
          {!ehCliente && iaVisivel && (
            <BotaoIcone className={styles.botaoTopo} data-tour="header-ai" titulo={!podeUsarAgente ? avisoSemPermissao : iaAtiva ? "Assistente de IA" : "Assistente de IA desativado"} onClick={() => onAbrirIA?.()} disabled={!iaAtiva || !podeUsarAgente}>
              <IcoIA />
            </BotaoIcone>
          )}
          {!ehCliente && <span className={styles.acoesDivider} aria-hidden="true" />}
          <BotaoIcone className={styles.botaoTopo} titulo={podeUsarChat ? traduzir("Chat interno") : avisoSemPermissao} onClick={abrirChat} disabled={!podeUsarChat}>
            <MessageCircle size={18} strokeWidth={1.7} />
            {naoLidasChat > 0 && <span className={styles.pontoNovidade} aria-hidden="true" />}
          </BotaoIcone>
          <BotaoIcone className={styles.botaoTopo} data-tour="header-notifications" titulo={podeUsarCentralNotificacoes ? traduzir("Notificações") : avisoSemPermissao} onClick={abrirNotificacoes} disabled={!podeUsarCentralNotificacoes}>
            <IcoSino />
            {totalNotificacoes > 0 && <span className={styles.pontoNovidade} aria-hidden="true" />}
          </BotaoIcone>
          <MenuUsuario />
        </div>
      </div>

      <FiltroPeriodoModal aberto={calendarioAberto} inicio={periodoGlobal?.inicio ?? ""} fim={periodoGlobal?.fim ?? ""} onFechar={() => setCalendarioAberto(false)} onAplicar={salvarPeriodoGlobal} onLimpar={limparPeriodoGlobal} />
    </header>
  );
}
